// client/src/components/checkin/CheckInForm.js
import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  TextField,
  Typography,
  Box,
  Snackbar,
  Alert,
} from '@mui/material';
import api from '../../services/api'; // Use the default export

const CheckInForm = ({ open, onClose, token }) => {
  const [moodRating, setMoodRating] = useState(3); // Default to middle value
  const [anxietyRating, setAnxietyRating] = useState(3); // Default to middle value
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleSliderChange = (setter) => (event, newValue) => {
    setter(newValue);
  };

  const handleNotesChange = (event) => {
    setNotes(event.target.value);
  };

  const resetForm = () => {
    setMoodRating(3);
    setAnxietyRating(3);
    setNotes('');
    setError('');
    setSuccess('');
    setLoading(false);
  };

  const handleCloseDialog = () => {
    resetForm();
    onClose(); // Call the parent's close handler
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const checkInData = { moodRating, anxietyRating, notes };
      await api.createCheckIn(checkInData, token);
      setSuccess('Check-in submitted successfully!');
      setSnackbarOpen(true);
      handleCloseDialog(); // Close dialog on success
    } catch (err) {
      console.error("Check-in submission error:", err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to submit check-in.';
      // Handle specific error for duplicate entry
      if (errorMessage.includes('already exists')) {
          setError('You have already submitted a check-in for today.');
      } else {
          setError(errorMessage);
      }
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
    setError('');
    setSuccess('');
  };

  const marks = [
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4' },
    { value: 5, label: '5' },
  ];

  return (
    <>
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Daily Check-In</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
            <Typography gutterBottom>Mood Rating (1=Very Low, 5=Very High)</Typography>
            <Slider
              aria-label="Mood Rating"
              value={moodRating}
              onChange={handleSliderChange(setMoodRating)}
              step={1}
              marks={marks}
              min={1}
              max={5}
              valueLabelDisplay="auto"
              sx={{ mb: 3 }}
            />

            <Typography gutterBottom>Anxiety Rating (1=Very Low, 5=Very High)</Typography>
            <Slider
              aria-label="Anxiety Rating"
              value={anxietyRating}
              onChange={handleSliderChange(setAnxietyRating)}
              step={1}
              marks={marks}
              min={1}
              max={5}
              valueLabelDisplay="auto"
              sx={{ mb: 3 }}
            />

            <TextField
              margin="normal"
              fullWidth
              id="notes"
              label="Optional Notes (How are you feeling today?)"
              name="notes"
              multiline
              rows={3}
              value={notes}
              onChange={handleNotesChange}
              inputProps={{ maxLength: 500 }} // Match backend limit
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">Cancel</Button>
          <Button
            type="submit" // Connects to form onSubmit
            onClick={handleSubmit} // Also trigger submit on button click
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit Check-In'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={error ? "error" : "success"} sx={{ width: '100%' }}>
          {error || success}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CheckInForm;
